import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from './components/dashboard/DashboardLayout';
import { SalesReport } from './components/reports/SalesReport';
import { ProductsReport } from './components/reports/ProductsReport';
import { StaffReport } from './components/reports/StaffReport';
import { TablesReport } from './components/reports/TablesReport';
import { ComparisonReport } from './components/reports/ComparisonReport';
import { OrderReport } from './components/reports/OrderReport';
import { CustomerReport } from './components/reports/CustomerReport';
import { TabsContent } from '../../components/ui/tabs';
import { COMPANY_ADMIN } from "../../common/SetupMasterEnum.js";
import { getDate } from "../../functions/dateFunctions";
import { postRequest } from '../../services/mainApp.service';
import { useSelector } from "react-redux";
import { saveAs } from 'file-saver';
import JSZip from 'jszip';

export default function DashboardPage() {
  const [dashboardData, setDashboardData] = useState(null);
  const [orderData, setOrderData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false)
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    endDate: new Date()
  });
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');

  const navigate = useNavigate();
  const appStore = useSelector((state) => state.authReducer);
  const { RoleId, dateFrom, dateTo, branchId } = useSelector((state) => state.authReducer);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await postRequest("PosDashboard", {
        OperationId: 1,
        CompanyId: appStore.CompanyId,
        BranchId:
          appStore.userBranchList?.length > 0 &&
            appStore.IsPos === true &&
            appStore.RoleId !== COMPANY_ADMIN
            ? appStore.userBranchList[0]?.BranchId
            : RoleId === 14
              ? branchId
              : null,
        DateFrom: RoleId === 14 ? dateFrom : getDate(dateRange.startDate),
        DateTo: RoleId === 14 ? dateTo : getDate(dateRange.endDate),
      });
      setDashboardData(response?.data?.DataSet);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const fetchOrderData = async () => {
    try {
      const response = await postRequest("Dashboard_Order", {
        OperationId: 1,
        CompanyId: appStore.CompanyId,
        BranchId:
          appStore.userBranchList?.length > 0 &&
            appStore.IsPos === true &&
            appStore.RoleId !== COMPANY_ADMIN
            ? appStore.userBranchList[0]?.BranchId
            : RoleId === 14
              ? branchId
              : null,
        DateFrom: RoleId === 14 ? dateFrom : getDate(dateRange.startDate),
        DateTo: RoleId === 14 ? dateTo : getDate(dateRange.endDate),
      });
      setOrderData(response?.data.Data || []);
    } catch (error) {
      console.error('Error fetching order data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllData = async () => {
    await Promise.all([fetchDashboardData(), fetchOrderData()]);
  };

  useEffect(() => {
    if (appStore.CompanyId) {
      fetchAllData();
    }
  }, [appStore.CompanyId, dateRange, selectedBranch]);

  const handleDateChange = (dates) => {
    setDateRange(dates);
  };

  const handleBranchChange = (branchId) => {
    setSelectedBranch(branchId);
  };

  const handleRefresh = () => {
    fetchAllData();
  };

  const handleExport = async (type) => {
    try {
      setExporting(true);

      if (type === 'all') {
        await exportAllReports();
        return;
      }

      // Export single report
      exportSingleReport(type);

    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setExporting(false);
    }
  };

  // Helper function to convert data to CSV
  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';

    const headers = Object.keys(data[0]);
    const csvRows = [
      headers.join(','),
      ...data.map(row =>
        headers.map(header => {
          const value = row[header];
          if (value === null || value === undefined) return '';
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
      )
    ];

    return csvRows.join('\n');
  };

  // Export single report
  const exportSingleReport = (reportType) => {
    let csvData = '';
    let filename = '';

    switch (reportType) {
      case 'orders':
        if (orderData?.length > 0) {
          csvData = convertToCSV(orderData);
          filename = 'orders';
        }
        break;

      case 'sales':
      case 'overview':
        if (dashboardData?.Table?.[0]) {
          const salesData = dashboardData.Table[0];
          const summaryData = [
            { Metric: 'Gross Sales', Value: salesData.Gross || 0 },
            { Metric: 'Net Sales', Value: salesData.NetSale || 0 },
            { Metric: 'Tax', Value: salesData.Tax || 0 },
            { Metric: 'Discount', Value: salesData.Discount || 0 },
            { Metric: 'Delivery Charges', Value: salesData.DeliveryCharges || 0 },
            { Metric: 'Total Orders', Value: salesData.TotalOrders_Landed || 0 },
            { Metric: 'Cash Sales', Value: salesData.Cash || 0 },
            { Metric: 'Card Sales', Value: salesData.Card || 0 },
            { Metric: 'Online Sales', Value: salesData.Online || 0 },
          ];
          csvData = convertToCSV(summaryData);
          filename = 'sales_summary';
        }
        break;

      case 'products':
        // Check for products data - adjust based on your actual data structure
        const productsData = dashboardData?.Products || dashboardData?.Table1 || [];
        if (productsData.length > 0) {
          csvData = convertToCSV(productsData);
          filename = 'products';
        }
        break;

      case 'customers':
        // Extract customer data from orders
        if (orderData?.length > 0) {
          const customerMap = {};
          orderData.forEach(order => {
            const customerId = order.CustomerId || order.CustomerName || 'Guest';
            if (!customerMap[customerId]) {
              customerMap[customerId] = {
                Customer: order.CustomerName || 'Guest',
                TotalOrders: 0,
                TotalSpent: 0,
                Phone: order.CustomerPhone || '',
                Email: order.CustomerEmail || '',
              };
            }
            customerMap[customerId].TotalOrders += 1;
            customerMap[customerId].TotalSpent += (order.NetAmount || 0);
          });

          const customerData = Object.values(customerMap).map(customer => ({
            ...customer,
            AverageOrderValue: customer.TotalOrders > 0 ? customer.TotalSpent / customer.TotalOrders : 0
          }));

          csvData = convertToCSV(customerData);
          filename = 'customers';
        }
        break;

      case 'staff':
        // Check for staff data - adjust based on your actual data structure
        const staffData = dashboardData?.Staff || dashboardData?.Table2 || [];
        if (staffData.length > 0) {
          csvData = convertToCSV(staffData);
          filename = 'staff';
        }
        break;

      case 'tables':
        // Check for table data
        const tablesData = dashboardData?.Tables || dashboardData?.Table3 || [];
        if (tablesData.length > 0) {
          csvData = convertToCSV(tablesData);
          filename = 'tables';
        }
        break;

      case 'comparison':
        // Create comparison data
        const comparisonData = [
          { Period: 'Current', GrossSales: dashboardData?.Table?.[0]?.Gross || 0, Orders: dashboardData?.Table?.[0]?.TotalOrders_Landed || 0 },
          { Period: 'Previous', GrossSales: dashboardData?.Table?.[1]?.Gross || 0, Orders: dashboardData?.Table?.[1]?.TotalOrders_Landed || 0 },
          { Period: 'Growth %', GrossSales: calculateGrowth(dashboardData?.Table?.[0]?.Gross || 0, dashboardData?.Table?.[1]?.Gross || 0), Orders: calculateGrowth(dashboardData?.Table?.[0]?.TotalOrders_Landed || 0, dashboardData?.Table?.[1]?.TotalOrders_Landed || 0) },
        ];
        csvData = convertToCSV(comparisonData);
        filename = 'comparison';
        break;

      default:
        console.log(`No export handler for ${reportType}`);
        return;
    }

    if (csvData) {
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    }
  };

  // Helper function for growth calculation
  const calculateGrowth = (current, previous) => {
    if (!previous || previous === 0) return 100;
    return ((current - previous) / previous) * 100;
  };

  // Batch export all reports
  const exportAllReports = async () => {
    try {
      const zip = new JSZip();
      const dateStr = new Date().toISOString().split('T')[0];
      const folder = zip.folder(`dashboard_reports_${dateStr}`);

      // 1. Sales Summary
      if (dashboardData?.Table?.[0]) {
        const salesData = dashboardData.Table[0];
        const summaryData = [
          { Metric: 'Gross Sales', Value: salesData.Gross || 0 },
          { Metric: 'Net Sales', Value: salesData.NetSale || 0 },
          { Metric: 'Tax', Value: salesData.Tax || 0 },
          { Metric: 'Discount', Value: salesData.Discount || 0 },
          { Metric: 'Delivery Charges', Value: salesData.DeliveryCharges || 0 },
          { Metric: 'Total Orders', Value: salesData.TotalOrders_Landed || 0 },
        ];
        folder.file('sales_summary.csv', convertToCSV(summaryData));
      }

      // 2. Orders Data
      if (orderData?.length > 0) {
        folder.file('orders.csv', convertToCSV(orderData));
      }

      // 3. Products Data
      const productsData = dashboardData?.Products || dashboardData?.Table1 || [];
      if (productsData.length > 0) {
        folder.file('products.csv', convertToCSV(productsData));
      }

      // 4. Customers Data
      if (orderData?.length > 0) {
        const customerMap = {};
        orderData.forEach(order => {
          const customerId = order.CustomerId || order.CustomerName || 'Guest';
          if (!customerMap[customerId]) {
            customerMap[customerId] = {
              Customer: order.CustomerName || 'Guest',
              TotalOrders: 0,
              TotalSpent: 0,
            };
          }
          customerMap[customerId].TotalOrders += 1;
          customerMap[customerId].TotalSpent += (order.NetAmount || 0);
        });

        const customerData = Object.values(customerMap).map(customer => ({
          ...customer,
          AverageOrderValue: customer.TotalOrders > 0 ? customer.TotalSpent / customer.TotalOrders : 0
        }));

        folder.file('customers.csv', convertToCSV(customerData));
      }

      // 5. Staff Data
      const staffData = dashboardData?.Staff || dashboardData?.Table2 || [];
      if (staffData.length > 0) {
        folder.file('staff.csv', convertToCSV(staffData));
      }

      // 6. Tables Data
      const tablesData = dashboardData?.Tables || dashboardData?.Table3 || [];
      if (tablesData.length > 0) {
        folder.file('tables.csv', convertToCSV(tablesData));
      }

      // 7. Comparison Data
      const comparisonData = [
        { Period: 'Current', GrossSales: dashboardData?.Table?.[0]?.Gross || 0, Orders: dashboardData?.Table?.[0]?.TotalOrders_Landed || 0 },
        { Period: 'Previous', GrossSales: dashboardData?.Table?.[1]?.Gross || 0, Orders: dashboardData?.Table?.[1]?.TotalOrders_Landed || 0 },
        { Period: 'Growth %', GrossSales: calculateGrowth(dashboardData?.Table?.[0]?.Gross || 0, dashboardData?.Table?.[1]?.Gross || 0), Orders: calculateGrowth(dashboardData?.Table?.[0]?.TotalOrders_Landed || 0, dashboardData?.Table?.[1]?.TotalOrders_Landed || 0) },
      ];
      folder.file('comparison.csv', convertToCSV(comparisonData));

      // 8. Export Metadata
      const metadata = {
        exportDate: new Date().toISOString(),
        dateRange: {
          start: dateRange.startDate.toISOString().split('T')[0],
          end: dateRange.endDate.toISOString().split('T')[0],
        },
        totalReports: 7,
        totalOrders: orderData?.length || 0,
        branch: selectedBranch === 'all' ? 'All Branches' : selectedBranch,
      };
      folder.file('export_metadata.json', JSON.stringify(metadata, null, 2));

      // Generate and download ZIP
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `dashboard_reports_${dateStr}.zip`);

    } catch (error) {
      console.error('Batch export failed:', error);
      throw error;
    }
  };


  const handleViewDetails = (reportType) => {
    navigate(`/dashboard/details/${reportType}`, {
      state: {
        dateRange,
        branch: selectedBranch,
        filters: {}
      }
    });
  };

  return (
    <DashboardLayout
      data={dashboardData}
      orderData={orderData}
      onDateChange={handleDateChange}
      onBranchChange={handleBranchChange}
      onRefresh={handleRefresh}
      loading={loading}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      onExport={handleExport}
      onViewDetails={handleViewDetails}
      exporting={exporting}>
      <TabsContent value="overview" className="space-y-4">
        <SalesReport
          data={dashboardData}
          orderData={orderData}
          onExport={() => handleExport('overview')}
          onViewDetails={() => handleViewDetails('overview')}
        />
      </TabsContent>

      <TabsContent value="sales" className="space-y-4">
        <SalesReport
          data={dashboardData}
          orderData={orderData}
          onExport={() => handleExport('sales')}
          onViewDetails={() => handleViewDetails('sales')}
        />
      </TabsContent>

      <TabsContent value="orders" className="space-y-4">
        <OrderReport
          data={dashboardData}
          orderData={orderData}
          onExport={() => handleExport('orders')}
          onViewDetails={() => handleViewDetails('orders')}
        />
      </TabsContent>

      <TabsContent value="products" className="space-y-4">
        <ProductsReport
          data={dashboardData}
          orderData={orderData}
          onExport={() => handleExport('products')}
          onViewDetails={() => handleViewDetails('products')}
        />
      </TabsContent>

      <TabsContent value="customers" className="space-y-4">
        <CustomerReport
          data={dashboardData}
          orderData={orderData}
          onExport={() => handleExport('customers')}
          onViewDetails={() => handleViewDetails('customers')}
        />
      </TabsContent>

      <TabsContent value="staff" className="space-y-4">
        <StaffReport
          data={dashboardData}
          orderData={orderData}
          onExport={() => handleExport('staff')}
          onViewDetails={() => handleViewDetails('staff')}
        />
      </TabsContent>

      <TabsContent value="tables" className="space-y-4">
        <TablesReport
          data={dashboardData}
          orderData={orderData}
          onExport={() => handleExport('tables')}
          onViewDetails={() => handleViewDetails('tables')}
        />
      </TabsContent>

      <TabsContent value="comparison" className="space-y-4">
        <ComparisonReport
          currentData={dashboardData}
          orderData={orderData}
          onExport={() => handleExport('comparison')}
          onViewDetails={() => handleViewDetails('comparison')}
        />
      </TabsContent>
    </DashboardLayout>
  );
}